import React from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// 定义数据类型
interface ChartData {
  typeDistribution: {
    name: string;
    value: number;
    percentage: number;
  }[];
  dailyTrend: {
    date: string;
    inbound: number;
    outbound: number;
  }[];
}

interface EChartsSectionProps {
  data?: ChartData;
}

// 默认数据
const defaultData = {
  typeDistribution: [
    { name: '放行', value: 85, percentage: 85 },
    { name: '待检', value: 10, percentage: 10 },
    { name: '异常', value: 5, percentage: 5 }
  ],
  dailyTrend: [
    { date: '1月', inbound: 2400, outbound: 2000 },
    { date: '2月', inbound: 1398, outbound: 1800 },
    { date: '3月', inbound: 9800, outbound: 7200 },
    { date: '4月', inbound: 3908, outbound: 4200 },
    { date: '5月', inbound: 4800, outbound: 3800 },
    { date: '6月', inbound: 3800, outbound: 4100 },
    { date: '7月', inbound: 4300, outbound: 3900 },
  ]
};

// ECharts主题配置
const theme = {
  color: ['#5470c6', '#91cc75', '#fac858', '#ee6666', '#73c0de', '#3ba272', '#fc8452', '#9a60b4', '#ea7ccc'],
  backgroundColor: '#f8fafc',
  textStyle: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  }
};

export function EChartsSection({ data }: EChartsSectionProps) {
  const chartData = data || defaultData;

  // 柱状图配置
  const getBarOption = () => ({
    title: {
      text: '数据分布统计',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1e293b'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: function(params: any) {
        const item = params[0];
        return `
          <div style="padding: 8px;">
            <div style="font-weight: bold; margin-bottom: 4px;">${item.name}</div>
            <div>数量: ${item.value}</div>
            <div>占比: ${chartData.typeDistribution.find(d => d.name === item.name)?.percentage || 0}%</div>
          </div>
        `;
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: chartData.typeDistribution.map(item => item.name),
      axisLabel: {
        color: '#64748b'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#64748b'
      },
      splitLine: {
        lineStyle: {
          color: '#e2e8f0'
        }
      }
    },
    series: [{
      type: 'bar',
      data: chartData.typeDistribution.map(item => ({
        value: item.value,
        itemStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: '#5470c6' },
            { offset: 1, color: '#91cc75' }
          ])
        }
      })),
      barWidth: '60%',
      borderRadius: [4, 4, 0, 0]
    }]
  });

  // 饼图配置
  const getPieOption = () => ({
    title: {
      text: '分布占比',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1e293b'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      bottom: '5%',
      left: 'center',
      textStyle: {
        color: '#64748b'
      }
    },
    series: [{
      name: '数据分布',
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['50%', '45%'],
      avoidLabelOverlap: false,
      label: {
        show: false,
        position: 'center'
      },
      emphasis: {
        label: {
          show: true,
          fontSize: '18',
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: chartData.typeDistribution.map((item, index) => ({
        value: item.percentage,
        name: item.name,
        itemStyle: {
          color: theme.color[index % theme.color.length]
        }
      }))
    }]
  });

  // 折线图配置
  const getLineOption = () => ({
    title: {
      text: '趋势分析',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1e293b'
      }
    },
    tooltip: {
      trigger: 'axis'
    },
    legend: {
      bottom: '5%',
      textStyle: {
        color: '#64748b'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData.dailyTrend.map(item => item.date),
      axisLabel: {
        color: '#64748b'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#64748b'
      },
      splitLine: {
        lineStyle: {
          color: '#e2e8f0'
        }
      }
    },
    series: [
      {
        name: '入库',
        type: 'line',
        smooth: true,
        data: chartData.dailyTrend.map(item => item.inbound),
        itemStyle: {
          color: '#5470c6'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(84, 112, 198, 0.3)' },
            { offset: 1, color: 'rgba(84, 112, 198, 0.1)' }
          ])
        }
      },
      {
        name: '出库',
        type: 'line',
        smooth: true,
        data: chartData.dailyTrend.map(item => item.outbound),
        itemStyle: {
          color: '#91cc75'
        },
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(145, 204, 117, 0.3)' },
            { offset: 1, color: 'rgba(145, 204, 117, 0.1)' }
          ])
        }
      }
    ]
  });

  // 面积图配置
  const getAreaOption = () => ({
    title: {
      text: '堆叠面积图',
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#1e293b'
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    legend: {
      bottom: '5%',
      textStyle: {
        color: '#64748b'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: chartData.dailyTrend.map(item => item.date),
      axisLabel: {
        color: '#64748b'
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: '#64748b'
      },
      splitLine: {
        lineStyle: {
          color: '#e2e8f0'
        }
      }
    },
    series: [
      {
        name: '入库',
        type: 'line',
        stack: 'Total',
        smooth: true,
        lineStyle: {
          width: 0
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(84, 112, 198, 0.8)' },
            { offset: 1, color: 'rgba(84, 112, 198, 0.3)' }
          ])
        },
        emphasis: {
          focus: 'series'
        },
        data: chartData.dailyTrend.map(item => item.inbound)
      },
      {
        name: '出库',
        type: 'line',
        stack: 'Total',
        smooth: true,
        lineStyle: {
          width: 0
        },
        showSymbol: false,
        areaStyle: {
          opacity: 0.8,
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: 'rgba(145, 204, 117, 0.8)' },
            { offset: 1, color: 'rgba(145, 204, 117, 0.3)' }
          ])
        },
        emphasis: {
          focus: 'series'
        },
        data: chartData.dailyTrend.map(item => item.outbound)
      }
    ]
  });

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* 趋势分析 */}
      <Card className="col-span-full lg:col-span-1 bg-cream-80">
        <CardHeader>
          <CardTitle className="text-16">数据趋势分析</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="area" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="area" className="text-14">面积图</TabsTrigger>
              <TabsTrigger value="line" className="text-14">折线图</TabsTrigger>
            </TabsList>
            
            <TabsContent value="area">
              <ReactECharts 
                option={getAreaOption()} 
                style={{ height: '300px' }}
                theme="light"
              />
            </TabsContent>
            
            <TabsContent value="line">
              <ReactECharts 
                option={getLineOption()} 
                style={{ height: '300px' }}
                theme="light"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* 分布统计 */}
      <Card className="bg-cream-80">
        <CardHeader>
          <CardTitle className="text-16">分布统计</CardTitle>
        </CardHeader>
        <CardContent>
          <ReactECharts 
            option={getBarOption()} 
            style={{ height: '300px' }}
            theme="light"
          />
        </CardContent>
      </Card>

      {/* 占比分析 */}
      <Card className="bg-cream-80">
        <CardHeader>
          <CardTitle className="text-16">占比分析</CardTitle>
        </CardHeader>
        <CardContent>
          <ReactECharts 
            option={getPieOption()} 
            style={{ height: '300px' }}
            theme="light"
          />
        </CardContent>
      </Card>

      {/* 对比分析 */}
      <Card className="bg-cream-80">
        <CardHeader>
          <CardTitle className="text-16">对比分析</CardTitle>
        </CardHeader>
        <CardContent>
          <ReactECharts 
            option={getLineOption()} 
            style={{ height: '300px' }}
            theme="light"
          />
        </CardContent>
      </Card>
    </div>
  );
}